import React, { useCallback, useState, useEffect } from 'react';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';

type Pharmacy = {
  id: number;
  name: string;
  address1: string;
  city: string;
  postCode: string;
  phone: string;
  lon: number;
  lat: number;
};

interface PharmacyMapProps {
  pharmacies: Pharmacy[];
  selectedPharmacy: Pharmacy | null;
  hoveredPharmacy: Pharmacy | null;
  onPharmacySelect: (pharmacy: Pharmacy) => void;
  postcodeCoordinates?: {
    lat: number;
    lng: number;
  };
}

const mapContainerStyle = {
  width: '100%',
  height: '100%',
  minHeight: '300px'
};

const defaultCenter = {
  lat: 51.5074, // London coordinates as default
  lng: -0.1278
};

// Update libraries to include both places and marker
const libraries: ("places" | "marker")[] = ["places", "marker"];

const PharmacyMap: React.FC<PharmacyMapProps> = ({ pharmacies, selectedPharmacy, hoveredPharmacy, onPharmacySelect, postcodeCoordinates }) => {
  const [selectedMarker, setSelectedMarker] = useState<Pharmacy | null>(null);
  const [error, setError] = useState<string | null>(null);

  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const googleMaps = typeof window !== 'undefined' ? window.google?.maps : undefined;
  const isGoogleMapsReady = !!googleMaps;

  useEffect(() => {
    if (!apiKey) {
      setError('Google Maps API key is missing');
    }
  }, [apiKey]);

  const onLoad = useCallback(() => {
    setError(null);
  }, []);

  const handleMarkerClick = (pharmacy: Pharmacy) => {
    setSelectedMarker(pharmacy);
  };

  const handleInfoWindowClose = () => {
    setSelectedMarker(null);
  };

  const handleInfoWindowClick = (pharmacy: Pharmacy, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    onPharmacySelect(pharmacy);
  };

  // Effect to handle selected pharmacy changes
  useEffect(() => {
    if (selectedPharmacy) {
      setSelectedMarker(selectedPharmacy);
    }
  }, [selectedPharmacy]);

  if (error) {
    return <div className="map-error">Error: {error}</div>;
  }

  if (!apiKey) {
    return <div className="map-error">Google Maps API key is missing</div>;
  }

  const getMarkerIcon = (isSelected: boolean, isHovered: boolean) => {
    if (!googleMaps) {
      return null;
    }

    return {
      path: googleMaps.SymbolPath.CIRCLE,
      scale: isSelected ? 20 : isHovered ? 18 : 16,
      fillColor: isSelected ? '#FFFFFF' : isHovered ? '#FFFFFF' : '#163b21',
      fillOpacity: isSelected ? 1 : isHovered ? 0.9 : 0.9,
      strokeColor: '#163b21',
      strokeWeight: isSelected ? 3 : isHovered ? 2.5 : 2,
    };
  };

  const renderMarkers = () => {
    if (!isGoogleMapsReady || !pharmacies.length) {
      return null;
    }

    return pharmacies.map((pharmacy) => {
      if (!pharmacy.lat || !pharmacy.lon ||
        isNaN(pharmacy.lat) || isNaN(pharmacy.lon) ||
        pharmacy.lat < -90 || pharmacy.lat > 90 ||
        pharmacy.lon < -180 || pharmacy.lon > 180) {
        return null;
      }

      const isSelected = selectedPharmacy?.id === pharmacy.id;
      const isHovered = hoveredPharmacy?.id === pharmacy.id;

      const icon = getMarkerIcon(isSelected, isHovered);
      if (!icon) {
        return null;
      }

      return (
        <Marker
          key={pharmacy.id}
          position={{ lat: pharmacy.lat, lng: pharmacy.lon }}
          onClick={() => handleMarkerClick(pharmacy)}
          label={{
            text: 'CV',
            color: isSelected || isHovered ? '#163b21' : '#FFFFFF',
            fontWeight: 'bold',
            fontSize: isSelected ? '16px' : isHovered ? '15px' : '14px',
          }}
          icon={icon}
          animation={isSelected && googleMaps ? googleMaps.Animation.BOUNCE : undefined}
          zIndex={isSelected ? 1000 : isHovered ? 900 : 1}
          clickable={true}
          cursor="pointer"
        />
      );
    });
  };

  return (
    <LoadScript
      googleMapsApiKey={apiKey}
      libraries={libraries}
      onError={() => {
        setError('Failed to load Google Maps');
      }}
    >
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={postcodeCoordinates || (pharmacies.length > 0 ? { lat: pharmacies[0].lat, lng: pharmacies[0].lon } : defaultCenter)}
        zoom={pharmacies.length > 0 ? 15 : 13}
        onLoad={onLoad}
        options={{
          zoomControl: true,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
          gestureHandling: 'cooperative',
          clickableIcons: false,
        }}
      >
        {renderMarkers()}

        {selectedMarker && isGoogleMapsReady && (
          <InfoWindow
            position={{ lat: selectedMarker.lat, lng: selectedMarker.lon }}
            onCloseClick={handleInfoWindowClose}
            options={{
              ...(googleMaps ? { pixelOffset: new googleMaps.Size(0, -30) } : {}),
              disableAutoPan: true
            }}
          >
            <div
              style={{
                padding: '8px',
                cursor: 'pointer',
                userSelect: 'none'
              }}
            >
              <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>{selectedMarker.name}</h3>
              <p style={{ margin: '4px 0' }}>{selectedMarker.address1}</p>
              <p style={{ margin: '4px 0' }}>{selectedMarker.city}, {selectedMarker.postCode}</p>
              <p style={{ margin: '4px 0' }}>Phone: {selectedMarker.phone}</p>
              <button
                className="form-button view-availability-button"
                style={{
                  marginTop: '8px',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 16px',
                  backgroundColor: '#4285F4',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onClick={(e) => handleInfoWindowClick(selectedMarker, e)}
              >
                <span className="btn-text">View Availability</span>
                <span className="arrow-icon">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </button>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </LoadScript>
  );
};

export default PharmacyMap; 
